import _ from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import { getDeals as _getDeals } from '@/graphql/queries';
import { upsertDeal as _upsertDeal } from '@/graphql/mutations';
import HeaderWithTabs from '@/HeaderWithTabs'
import ErrorModal from '@/ErrorModal';
import Breadcrumb from '@/Breadcrumb';
import { selectField, textField } from '@/commonFields';
import useFormFields from '@/hooks/useFormFields';
import FilesList from '@/FilesList';

const client = generateClient();

const getObject = ({ id }) => {
	return client.graphql({ query: _getDeals, variables: { id } })
		.then(response => {
			const data = response.data.getDeals.deals?.[0];
			return data;
		});
};

const upsertObject = ({ input }) => {
	return client.graphql({ query: _upsertDeal, variables: { input } })
		.then(response => {
			return response.data.upsertDeal;
		});
};

export default ({ createLabel = 'Create', links = [], onCancel, onSave, updateLabel = 'Save' }) => {
	const {
		origModel,
		model,
		setModel,
		changeModel,
		deleteModel,
		jsonModel,
		errors,
		setErrors,
		hasValue,
		getError,
		get_value,
		set_value,
		get_array,
		set_array,
		add_array,
		remove_array,
		id,
		isCreate,
		sanitizeObject
	} = useFormFields();

	const isConstructed = useRef(false);
	const navigate = useNavigate();
	const [errorMessage, setErrorMessage] = useState();
	const [isLoaded, setIsLoaded] = useState(isCreate);
	const [isSaving, setIsSaving] = useState(false);
	const isEqual = _.isEqualWith(sanitizeObject(model), origModel.current);

	const [activeTab, _setActiveTab] = useState();
	const [filesActivated, setFilesActivated] = useState(false);
	const setActiveTab = tab => {
		const queryParams = new URLSearchParams(location.search);
		queryParams.set('tab', tab);
		navigate({ search: `?${queryParams.toString()}` }, { replace: true });
		_setActiveTab(tab);
		if (tab === 'files') {
			setFilesActivated(true);
		}
	};

	const catchError = error => {
		if (error instanceof Error) {
			setErrorMessage(error.toString());
		} else {
			setErrorMessage((error.errors || []).map(e => e.message?.toString()));
		}
		setIsLoaded(true);
		setIsSaving(false);
	};

	if (!isConstructed.current) {
		isConstructed.current = true;
		if (!isLoaded) {
			getObject({ id })
				.then(data => {
					origModel.current = { deals: data };
					setModel({ deals: data });
					setIsLoaded(true);
				})
				.catch(catchError);
		}
	}

	const details = useMemo(() => (
		<div className="row mt-5">
			<div className="col-6">
				<div className="border shadow p-3 bg-form">
					{textField({ autoFocus: isCreate, get: get_value, getError, set: set_value, key: 'deals', label: 'Deal Name', name: 'deal_name' })}
					{textField({ get: get_value, getError, set: set_value, key: 'deals', label: 'Deal Description', name: 'deal_description' })}
				</div>
			</div>
		</div>
	), [JSON.stringify(errors), JSON.stringify(model)]);

	const files = useMemo(() => filesActivated ? (
		<>
			<div className="row mt-5 g-0">
				<div className="col">
					<FilesList deal_id={id} />
				</div>
			</div>
		</>
	) : undefined, [filesActivated, JSON.stringify(errors), JSON.stringify(model)]);

	const tabs = [
		{ name: 'details', label: 'Details', content: details },
		...(!isCreate ? [{ name: 'files', label: 'Files', content: files }] : []),
	];

	const handleSave = () => {
		// Extract input from model
		const input = {
			...changeModel.current,
			deals: {
				...changeModel.current.deals,
				...(!isCreate ? { id } : undefined)
			}
		};
		console.log('model', model);
		const newErrors = {
			deals: {}
		};
		if (!hasValue({ key: 'deals', name: 'deal_name' })) {
			newErrors.deals.deal_name = 'Deal Name is required';
		}
		// Clean out empties
		Object.keys(newErrors).forEach(e => {
			if (
				(Array.isArray(newErrors[e]) && newErrors[e].length === 0) ||
				Object.keys(newErrors[e]).length === 0
			) {
				delete newErrors[e];
			}
		});
		console.log('newErrors', newErrors);
		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}
		console.log('input', input);
		console.log('deleteModel', deleteModel.current);
		setIsSaving(true);
		upsertObject({ input, ...(deleteModel.current.length > 0 ? { input_delete: deleteModel.current } : undefined) })
			.then(response => {
				console.log('response', response);
				setIsSaving(false);
				onSave && onSave();
			})
			.catch(catchError);
	};

	const handleCancel = () => onCancel && onCancel();

	return (
		<>
			{errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage()} title="Error" />}
			<div className="container-fluid px-4 position-relative">
				<HeaderWithTabs
					isLoaded={isLoaded}
					onChange={setActiveTab}
					path="/app/customers/"
					tabs={tabs}
				>
					<>
						{!isLoaded ? (
							<div className="text-center" style={{marginTop:'5rem'}}>
								<button className="btn btn-primary" type="button" disabled>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Loading...
								</button>
							</div>
						) : (
							<>
								{links.length > 0 && <Breadcrumb className="mb-3 px-3" links={links} />}
								<div className="row align-items-center position-absolute py-1 me-5" style={{right:0}}>
									<button className="col-auto ml-auto my-0 btn btn-primary" disabled={!((isCreate || !isEqual) && !isSaving)} onClick={handleSave}>{isCreate ? createLabel : updateLabel }</button>
									<a className="col-auto" role="button" onClick={handleCancel}>Cancel</a>
								</div>
								{isSaving && (
									<div className="text-center position-absolute" style={{height:'100%',left:0,top:0, width:'100%',zIndex:1}}>
										<button className="btn btn-primary position-relative" style={{top:'40%'}} type="button" disabled>
											<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
											Saving...
										</button>
									</div>
								)}
							</>
						)}
					</>
				</HeaderWithTabs>
			</div>
		</>
	);
};
